/**
 * ArchLang v0.10 — selector evaluator, slice 1 (P1b-1): the **node universe**
 * and node-sort **atom + boolean-combinator** evaluation.
 *
 * A selector evaluates over the resolved model + derived graph to a node set or
 * an edge set (design §1). This slice builds the node universe (every named
 * element — modules, surfaces, interfaces, processes, subprocesses, spaces —
 * with its structural kind, type chain, and resolved aspects) and evaluates the
 * node-sort atoms (`*`, built-in kinds, user types incl. subtypes, element
 * refs, `@@key[:value]`) composed with `and`/`or`/`not`.
 *
 * Knob references (§3, S18) resolve against `ctx.knobs`: the `$knob` ELEMENT atom
 * (`evalKnobAtom`) resolves to its bound element, `@@key:$knob` (`aspectAtomValue`)
 * to the bound aspect value, and `within $knob` (`withinValue`) to the bound closure
 * bound. An unbound / wrong-kind knob is a targeted error, never a silent wrong set.
 *
 * DEFERRED to later slices (kept explicit — an unsupported node throws so a caller
 * can't silently get a wrong set): `on <plane>` membership (handled at projection),
 * edge-side `where`, and `outside` (resolved by `view-projection.ts` against the
 * final shown set). (`violating <P>` IS supported — see `evalViolating`.)
 */

import type { Value } from "./types.js";
import type { QualifiedName, SourceSpan } from "./types.js";
import type { ResolvedInterface, ResolvedModel, ResolvedModule, ResolvedSurface } from "./resolver.js";
import type { DependencyEdge, DependencyGraph } from "./graph.js";
import type { Closure, ClosureMode } from "./closure.js";
import { moduleKey } from "./closure.js";
import { forEachAspectValue } from "./aspects.js";
import type { DecorationInstance, DecorationRegistry, LifecycleWindow } from "./decorations.js";
import { EMPTY_TIMELINE } from "./timeline.js";
import {
  argValue,
  buildDecorationRegistry,
  clampToContainer,
  isDecoratedWindow,
  lifecycleStateAt,
  primaryFieldOf,
  windowFromDecorations,
} from "./decorations.js";
import { buildSpaceIndex, spaceContains } from "./spaces.js";
import type { PresenceInfo } from "./presence.js";
import { evalExpression, isTruthy } from "./expression-eval.js";
// Cyclic (policy-eval imports evalSelector): runtime-safe — only called, never
// used at module-init. Powers the `violating <P>` atom.
import { evalPolicy, activeFindings } from "./policy-eval.js";
import type { PolicyDecl, QueryDecl, StyleBundleDecl, TestDecl } from "./v010-ast.js";
import { canonWhen, canonThen } from "./process-test-canon.js";
import type { Selector, SelAspect, SelKnob, SelRef, SelViolating, WithinBound, Expr } from "./selector-ast.js";
import type { CapabilityEvalEnv } from "./capability-registry.js";

/** The structural kind an element occupies in the node universe (design §1). */
export type StructuralKind = "module" | "surface" | "interface" | "process" | "subprocess" | "space" | "test";

/** The built-in kind atoms — matched by structural kind, not by user type. */
export const BUILTIN_KINDS: ReadonlySet<string> = new Set([
  "module",
  "surface",
  "interface",
  "process",
  "subprocess",
  "space",
  // A `test` is a selectable subject so governance policies can gate on test
  // state and a changed `then` fires a §10 `when … changed` gate (process-tests
  // P9). Like process/subprocess it is not a board element — view projection
  // frames only `module` kinds, so `show *` never paints it.
  "test",
]);

/** One materialized element in the node universe. */
export interface ElementNode {
  /** Dotted qualified path — the set-membership key. */
  readonly key: string;
  readonly path: QualifiedName;
  readonly structuralKind: StructuralKind;
  /** The element's own user type (undefined for spaces). */
  readonly typeName?: string;
  /** `[typeName, …ancestor types]` — subtype matching tests membership here. */
  readonly typeChain: readonly string[];
  /** Resolved aspects: dotted key-path → the values carried (list-flattened). */
  readonly aspects: ReadonlyMap<string, readonly Value[]>;
  /** Resolved field values: dotted field-path → value (for `@field` getters). */
  readonly fields: ReadonlyMap<string, Value>;
  /** Decorations written on this element, keyed by decoration name. Feeds the
   *  `@deco` atom, the `@deco.field` getter, and the `@lifecycle` builtin. */
  readonly decorations?: ReadonlyMap<string, DecorationInstance>;
  /** This element's lifecycle window, already clamped to its containers.
   *  Undefined when nothing in its chain carries a lifecycle decoration. */
  readonly lifecycle?: LifecycleWindow;
  /** Declaring space (undefined = package root / space node itself). */
  readonly space?: QualifiedName;
  /** The element's declaration span (its name identifier) — anchors findings /
   *  diagnostics in the editor. Undefined for spaces (no single decl site). */
  readonly source?: SourceSpan;
}

export interface NodeUniverse {
  readonly nodes: readonly ElementNode[];
  /** The model timeline's `now` ordinal — the scrub point every lifecycle read
   *  falls back to when no `at` clause and no explicit scrub point is wired. */
  readonly now: number;
  /** Every decoration in scope. Carried here because the getter and atom
   *  evaluators need a definition to know which field a bare positional binds —
   *  hardcoding one is how `@deprecated("v3")` silently stopped matching when
   *  the primary moved from `to` to `from`. */
  readonly decorations: DecorationRegistry;
  byKey(key: string): ElementNode | undefined;
  /** Resolve an element ref by qualified path, else by unique short name. */
  resolveRef(path: QualifiedName): ElementNode | undefined;
  /** Is `name` a registered user type (→ a type atom, not an element ref)? */
  isTypeName(name: string): boolean;
}

// =============================================================================
// Node universe construction
// =============================================================================

/** Memo for {@link lifecycleWindowsOf}. A model is immutable and replaced
 *  wholesale on every edit, so a `WeakMap` keyed on it is both safe and
 *  self-evicting. */
const windowsCache = new WeakMap<ResolvedModel, ReadonlyMap<string, LifecycleWindow>>();

/**
 * Every element's effective (container-clamped) lifecycle window, keyed the way
 * the board keys its nodes.
 *
 * The ONE source of windows, for every surface: the board, the inspector and
 * the headless renderer all read this. A bare board is a view with no clauses
 * at all, and it still has to paint and still has to show the scrubber. Empty
 * when nothing in the model carries a lifecycle decoration, which is the signal
 * every consumer uses to stay entirely off screen.
 *
 * Cached because being the one source means several consumers ask for it off
 * the same model, and each call builds a whole node universe.
 */
export function lifecycleWindowsOf(model: ResolvedModel): ReadonlyMap<string, LifecycleWindow> {
  const hit = windowsCache.get(model);
  if (hit) return hit;
  const out = new Map<string, LifecycleWindow>();
  const idOf = new Map<string, string>();
  const spaceOf = new Map<string, string>();
  walkResolvedModules(model.modules, (m, path) => {
    const key = path.join(".");
    if (m.decl.id) idOf.set(key, m.decl.id);
    const s = m.space?.join(".");
    if (s) spaceOf.set(key, s);
  });

  for (const node of buildNodeUniverse(model).nodes) {
    if (!node.lifecycle) continue;
    // The universe keys on the qualified dotted path, but the BOARD keys a node
    // through `moduleKey` — its STABLE ID when it has one, else its
    // `Space::Qualified.Path` form. Publishing every alias is what makes the
    // live lookup hit: keyed on the universe form alone, a node carrying a
    // stable id (or sitting in a space) found no paint and rendered as though
    // it had no lifecycle at all.
    out.set(node.key, node.lifecycle);
    const id = idOf.get(node.key);
    if (id) out.set(id, node.lifecycle);
    const space = spaceOf.get(node.key);
    if (space) out.set(`${space}::${node.key}`, node.lifecycle);
  }
  windowsCache.set(model, out);
  return out;
}

/** Depth-first walk of the resolved module tree, carrying each module's path. */
function walkResolvedModules(
  modules: readonly ResolvedModule[],
  visit: (m: ResolvedModule, path: readonly string[]) => void,
  parent: readonly string[] = [],
): void {
  for (const m of modules) {
    const path = [...parent, m.name];
    visit(m, path);
    walkResolvedModules(m.modules, visit, path);
  }
}

export function buildNodeUniverse(model: ResolvedModel): NodeUniverse {
  const nodes: ElementNode[] = [];
  const byKey = new Map<string, ElementNode>();
  const byShortName = new Map<string, ElementNode[]>();

  // Resolved aspects per element, keyed by owner path (aspects.ts already walks
  // modules/surfaces/interfaces AND processes, list-flattening values).
  const aspectsByOwner = new Map<string, Map<string, Value[]>>();
  forEachAspectValue(model, (occ) => {
    const ownerKey = occ.ownerPath.join(".");
    let m = aspectsByOwner.get(ownerKey);
    if (!m) {
      m = new Map();
      aspectsByOwner.set(ownerKey, m);
    }
    const keyPath = occ.keyPath.join(".");
    const arr = m.get(keyPath) ?? [];
    arr.push(occ.value);
    m.set(keyPath, arr);
  });

  const spaceIndex = buildSpaceIndex(model);

  const typeChainOf = (typeName: string): string[] => {
    const t = model.registry.kinds.get(typeName);
    return [typeName, ...(t?.chain ?? [])];
  };

  const fieldsOf = (body: { fields: readonly { path: QualifiedName; value?: Value }[] } | undefined): Map<string, Value> => {
    const m = new Map<string, Value>();
    for (const f of body?.fields ?? []) if (f.value) m.set(f.path.join("."), f.value);
    return m;
  };

  const decorationRegistry = model.decorationRegistry ?? buildDecorationRegistry([]);
  const timeline = model.timeline ?? EMPTY_TIMELINE;
  // A node's window is its own decorations clamped by every container above it,
  // so `@lifecycle` answers with the EFFECTIVE state — a child of a retired
  // parent is retired even with no decoration of its own.
  const windowByKey = new Map<string, LifecycleWindow>();

  const add = (
    path: QualifiedName,
    structuralKind: StructuralKind,
    typeName: string | undefined,
    space: QualifiedName | undefined,
    fields: Map<string, Value>,
    source?: SourceSpan,
    decorations?: readonly DecorationInstance[],
  ): void => {
    const key = path.join(".");
    let decoMap: Map<string, DecorationInstance> | undefined;
    if (decorations && decorations.length > 0) {
      decoMap = new Map();
      for (const d of decorations) decoMap.set(d.name, d);
    }
    let lifecycle = windowFromDecorations(decorations ?? [], decorationRegistry, timeline);
    for (let i = path.length - 1; i > 0; i--) {
      const up = windowByKey.get(path.slice(0, i).join("."));
      if (up) lifecycle = clampToContainer(lifecycle, up);
    }
    const decorated = isDecoratedWindow(lifecycle);
    if (decorated) windowByKey.set(key, lifecycle);
    const node: ElementNode = {
      key,
      path,
      structuralKind,
      typeName,
      typeChain: typeName ? typeChainOf(typeName) : [],
      aspects: aspectsByOwner.get(key) ?? EMPTY_ASPECTS,
      fields,
      ...(decoMap ? { decorations: decoMap } : {}),
      ...(decorated ? { lifecycle } : {}),
      space,
      source,
    };
    nodes.push(node);
    // First declaration of a key wins (twins are a validator concern).
    if (!byKey.has(key)) byKey.set(key, node);
    const short = path[path.length - 1]!;
    const list = byShortName.get(short) ?? [];
    list.push(node);
    byShortName.set(short, list);
  };

  const visitInterface = (i: ResolvedInterface, parent: QualifiedName, space: QualifiedName | undefined): void => {
    add([...parent, i.name], "interface", i.typeName, space, fieldsOf(i.body), i.decl.nameSource ?? i.decl.source, i.decl.decorations);
  };
  const visitSurface = (f: ResolvedSurface, parent: QualifiedName, space: QualifiedName | undefined): void => {
    const myPath = [...parent, f.name];
    add(myPath, "surface", f.typeName, space, fieldsOf(f.body), f.decl.nameSource ?? f.decl.source, f.decl.decorations);
    for (const i of f.interfaces) visitInterface(i, myPath, space);
    for (const sub of f.surfaces) visitSurface(sub, myPath, space);
  };
  const visitModule = (m: ResolvedModule, parent: QualifiedName): void => {
    const myPath = [...parent, m.name];
    // Nesting is lexical (one file), so a subtree shares the module's space.
    const space = spaceIndex.spaceOfModule(m);
    add(myPath, "module", m.typeName, space, fieldsOf(m.body), m.decl.nameSource ?? m.decl.source, m.decl.decorations);
    for (const i of m.interfaces) visitInterface(i, myPath, space);
    for (const f of m.surfaces) visitSurface(f, myPath, space);
    for (const child of m.modules) visitModule(child, myPath);
  };
  for (const m of model.modules) visitModule(m, []);

  // Processes / subprocesses (named; anonymous carry no name → unselectable).
  // Space follows the owning module (via the already-built node index).
  const spaceOfOwner = (ownerPath: QualifiedName | undefined): QualifiedName | undefined =>
    ownerPath && ownerPath.length ? byKey.get(ownerPath.join("."))?.space : undefined;
  // Decorations ride along so a process resolves a lifecycle window like any
  // other node. Omitting them meant `@planned(v3) process Rollout` put `v3` on
  // the axis and read `planned` in the inspector while every selector — and so
  // the board — saw it as live: a scrubber stop nothing reacted to.
  for (const pm of model.processModels ?? []) {
    if (!pm.name) continue;
    add([...(pm.ownerPath ?? []), pm.name], "process", undefined, spaceOfOwner(pm.ownerPath), fieldsOf(pm.fields ? { fields: pm.fields } : undefined), undefined, pm.decorations);
  }
  for (const sm of model.subprocessModels ?? []) {
    if (!sm.name) continue;
    add([...(sm.ownerPath ?? []), sm.name], "subprocess", undefined, spaceOfOwner(sm.ownerPath), new Map(), undefined, sm.decorations);
  }

  // Tests (process-tests P9) — a `test` element anchors the diff by its stable
  // `#id` (falling back to its title), so a rename of the descriptive name reads
  // as `changed`, not remove+add, and its `when`/`then` canonical strings feed
  // the §10 `changed(@when|@then)` gate. Key segment = `#<id>` when present.
  for (const t of collectTestDecls(model)) {
    const seg = t.id ? `#${t.id}` : t.name;
    const fields = fieldsOf({ fields: t.fields });
    if (t.target && t.target.length) fields.set("of", { kind: "string", text: t.target.join(".") });
    fields.set("when", { kind: "string", text: canonWhen(t.pins) });
    fields.set("then", { kind: "string", text: canonThen(t.assertions) });
    add(
      [...(t.ownerPath ?? []), seg],
      "test",
      undefined,
      spaceOfOwner(t.ownerPath),
      fields,
      t.nameSource ?? t.source,
    );
  }

  // Spaces as elements (design §1) — deduped qualified names.
  const seenSpace = new Set<string>();
  for (const qn of model.spaces?.values() ?? []) {
    const k = qn.join(".");
    if (k === "" || seenSpace.has(k)) continue;
    seenSpace.add(k);
    add(qn, "space", undefined, undefined, new Map());
  }

  // A name reads as a TYPE atom if it is a registered kind OR it appears as a
  // type in some element's typeChain. The instance side is the source of truth:
  // an imported/stdlib kind whose synthetic decl reached the name-keyed registry
  // is covered by the first clause, but a kind a module CARRIES while its `type`
  // declaration never landed in `registry.kinds` — an unresolved/omitted import,
  // or the UNKNOWN_TYPE default — is only reachable through the chain the node
  // already stamped. Recognition must stay in lockstep with matching
  // (`evalRefOrType` tests `n.typeChain.includes(name)`), else such an atom
  // silently falls through to element-ref resolution and empties to nothing.
  // (Base-kind names like `module` also land here harmlessly — `evalRefOrType`
  // and `evalRef` test `BUILTIN_KINDS` before ever consulting `isTypeName`.)
  const typeNamesInUse = new Set<string>();
  for (const n of nodes) for (const t of n.typeChain) typeNamesInUse.add(t);

  return {
    nodes,
    now: timeline.now,
    decorations: decorationRegistry,
    byKey: (key) => byKey.get(key),
    resolveRef: (path) => {
      const key = path.join(".");
      const exact = byKey.get(key);
      if (exact) return exact;
      if (path.length === 1) {
        const list = byShortName.get(path[0]!);
        if (list && list.length === 1) return list[0];
      }
      return undefined;
    },
    isTypeName: (name) => model.registry.kinds.has(name) || typeNamesInUse.has(name),
  };
}

const EMPTY_ASPECTS: ReadonlyMap<string, readonly Value[]> = new Map();

/** Every `test` reachable from a resolved model: top-level + `in`-placed tests
 *  hoisted onto `model.tests`, plus each module body's tests (recursively). The
 *  same collection the batch runner walks — kept inline here to avoid a cycle
 *  through `process-test-run.ts`. */
function collectTestDecls(model: ResolvedModel): readonly TestDecl[] {
  const out: TestDecl[] = [...(model.tests ?? [])];
  const visit = (m: ResolvedModule): void => {
    if (m.body.tests) out.push(...m.body.tests);
    for (const c of m.modules) visit(c);
  };
  for (const m of model.modules) visit(m);
  return out;
}

// =============================================================================
// Node-sort selector evaluation (atoms + and/or/not)
// =============================================================================

/** A node deferred to a later evaluator slice — thrown, not silently empty. */
export class UnsupportedSelectorError extends Error {
  constructor(readonly node: Selector) {
    super(`selector node '${node.kind}' is not evaluated in this slice`);
    this.name = "UnsupportedSelectorError";
  }
}

/** A sort mismatch (`node and edge`, cross-sort `or`) — a targeted diagnostic. */
export class SortMismatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SortMismatchError";
  }
}

/** A selector's value: a node set (element keys) or an edge set (graph edges). */
export type EvalResult =
  | { readonly sort: "node"; readonly nodes: Set<string> }
  | { readonly sort: "edge"; readonly edges: Set<DependencyEdge> };

export interface EvalContext {
  readonly universe: NodeUniverse;
  /** The derived graph — required for edge patterns / extraction. */
  readonly graph?: DependencyGraph;
  /** The distance closure — required for `>>` / `<>` (`within`) patterns. */
  readonly closure?: Closure;
  /** The bound subject — resolves `this` / `in this` and getters in `where`. */
  readonly subject?: ElementNode;
  /**
   * The declaring module's qualified path for a module-scoped view/policy
   * (v0.10 §5.2, the §5.5 module-scoped-process precedent). A bare element
   * `ref` resolves from this context first — the owner chain walked outward
   * (`[...scope, ...path]`, then the scope's parent, …) — before falling back
   * to global resolution. Undefined (every top-level declaration) is
   * byte-identical to pre-scoping behavior.
   */
  readonly scopePath?: QualifiedName;
  /** Named policies in scope — required to evaluate a `violating <P>` atom. */
  readonly policies?: ReadonlyMap<string, PolicyDecl>;
  /** Named queries in scope — a bare `ref` to one of these names inlines its
   *  selector (§1.9). For an imported policy this is the HOME package's query
   *  vocabulary (the lexical query closure): the query DEFINITION resolves in the
   *  home package while the DATA it evaluates over is the importer's model. */
  readonly queries?: ReadonlyMap<string, QueryDecl>;
  /** Named style bundles in scope — required to expand a `use <Bundle>` member
   *  inside a `style` body (S14). */
  readonly styleBundles?: ReadonlyMap<string, StyleBundleDecl>;
  /** The gate clock for `violating <P>` waiver expiry (§4). Set ONLY on the
   *  change-gate path; undefined everywhere else (render/LSP) so waivers stay
   *  timeless — `evalViolating` forwards it to `evalPolicy`. */
  readonly now?: Date;
  /**
   * The timeline scrub point `@lifecycle` resolves against, as comparable time.
   *
   * A TIMELINE ORDINAL, not an instant — see `timeline.ts`. Injected the same
   * way `processLatency` / `processTest` are — neutral when unwired, so every
   * existing caller keeps working. Absent means **now**: a
   * policy, a lens, or a view with no `at` clause reads the model as it stands
   * today rather than refusing to answer.
   */
  readonly scrubAt?: number;
  /** The capability layer (§5) — the registry + injected providers (`ver` via
   *  git) + the absent-capability sink. Undefined = no capability data wired
   *  (render/LSP default): a capability getter routes to an absent finding and
   *  yields `undefined`, never a throw. */
  readonly capabilities?: CapabilityEvalEnv;
  /** Plane presence (§1.2) — required for a view's `on <plane>` membership subset
   *  (S16). Undefined = no plane data (an `on` clause no-ops with a diagnostic).
   *  Keyed by dot-joined module path → its plane-name set. */
  readonly presence?: PresenceInfo;
  /**
   * The plane a board falls back to when the view names none (§1.2) — the
   * boards' shared default, set by viewer-core's plane seam so the live shell
   * and the headless backends narrow identically.
   *
   * NOT an `on <plane>` clause. That one asks "show me this plane" and deletes
   * every node absent from it, which takes a plane-scoped module (`on
   * deployment service Euro`) off the DEFAULT board the moment some unrelated
   * module happens to use a partial block. The default board asks the narrower
   * question — "hide what exists ONLY inside a plane block" — so it drops
   * plane-exclusive members and nothing else. A view's own `on` clause wins.
   */
  readonly defaultPlane?: string;
  /** Bound knob values in scope (§3) — resolves `within $knob` (and `$knob`
   *  atoms) against the value a view/instance binds. Undefined = no knobs bound
   *  (an unbound `$knob` is a targeted error, never silent). Keyed by knob name. */
  readonly knobs?: ReadonlyMap<string, Value>;
  /** Internal: the chain of query names currently being expanded, so a cyclic
   *  `query A: B` / `query B: A` (or self-reference) is caught rather than
   *  recursing forever. Grows as `evalSelector` descends into a query body. */
  readonly _queryStack?: ReadonlySet<string>;
  /** v0.10.2 latency analysis (§10): subject key → its latency getters, for the
   *  `@estimatedLatency[.…]` builtin. Undefined = not wired (the builtin then
   *  reads neutral: `undefined` / `.bounded` = true), so render/LSP without a
   *  latency pass behave exactly as before. `buildProcessLatencyResolver`. */
  readonly processLatency?: import("./process-latency.js").ProcessLatencyResolver;
  /** Process-tests governance (P9): subject key → its test getters, for the
   *  `@tested` / `@testCoverage` builtins. Undefined = not wired (the builtins
   *  then read neutral: `undefined`), so render/LSP without a test pass behave
   *  exactly as before. `buildProcessTestResolver`. */
  readonly processTest?: import("./process-test-canon.js").ProcessTestResolver;
}

/**
 * Evaluate a selector to its (sort-carrying) result. Slice 3a supports node
 * atoms + `and`/`or`/`not` + structural `in`, edge **adjacency** arrows (`>`,
 * chains), and extraction (`sources/targets/nodes of`). Deferred (throws):
 * closure arrows (`>>`/`<>` `within`), `owners of`, `on`/`where`/`this`/`$knob`/
 * `outside`/`violating`, edge predicates, and `in <process>`.
 */
export function evalSelector(sel: Selector, ctx: EvalContext): EvalResult {
  const u = ctx.universe;
  switch (sel.kind) {
    case "universal":
      return node(new Set(u.nodes.map((n) => n.key)));
    case "ref":
      return evalRef(sel, ctx);
    case "aspect":
      return node(evalAspect(sel.path, aspectAtomValue(sel, ctx), u));
    case "decoration":
      return node(evalDecorationAtom(sel.name, sel.value, u, ctx));
    case "knob":
      return node(evalKnobAtom(sel, ctx));
    case "this": {
      if (!ctx.subject) throw new UnsupportedSelectorError(sel);
      return node(new Set([ctx.subject.key]));
    }
    case "in":
      return node(evalIn(sel.arg, ctx, sel.depth));
    case "depth":
      return node(evalDepth(boundValue(sel.bound, ctx, "depth"), ctx));
    case "where":
      return node(evalWhere(sel.expr, ctx));
    case "arrow":
      return edge(evalArrow(sel, ctx));
    case "extraction":
      return node(evalExtraction(sel, ctx));
    case "violating":
      return evalViolating(sel, ctx);
    case "and":
      return evalAndOr(sel.terms, ctx, "and");
    case "or":
      return evalAndOr(sel.terms, ctx, "or");
    case "not": {
      const inner = evalSelector(sel.term, ctx);
      if (inner.sort === "node") {
        return node(new Set(u.nodes.map((n) => n.key).filter((k) => !inner.nodes.has(k))));
      }
      const all = graphOf(ctx).edges;
      return edge(new Set(all.filter((e) => !inner.edges.has(e))));
    }
    default:
      throw new UnsupportedSelectorError(sel);
  }
}

/**
 * Node-only convenience (slice 1/2 callers): evaluate WITHOUT a graph and assert
 * a node-sort result. An edge pattern throws {@link UnsupportedSelectorError}.
 */
export function evalNodeSelector(sel: Selector, u: NodeUniverse): Set<string> {
  const r = evalSelector(sel, { universe: u });
  if (r.sort !== "node") throw new SortMismatchError("expected a node-sort selector, got an edge set");
  return r.nodes;
}

const node = (nodes: Set<string>): EvalResult => ({ sort: "node", nodes });
const edge = (edges: Set<DependencyEdge>): EvalResult => ({ sort: "edge", edges });

function graphOf(ctx: EvalContext): DependencyGraph {
  if (!ctx.graph) throw new SortMismatchError("an edge pattern needs the derived graph in the eval context");
  return ctx.graph;
}

function closureOf(ctx: EvalContext): Closure {
  if (!ctx.closure) throw new SortMismatchError("`>>` / `<>` needs the distance closure in the eval context");
  return ctx.closure;
}

/** `and` / `or` over same-sort terms; predicate terms (`in`) are node-sort. */
function evalAndOr(terms: readonly Selector[], ctx: EvalContext, op: "and" | "or"): EvalResult {
  const parts = terms.map((t) => evalSelector(t, ctx));
  const sorts = new Set(parts.map((p) => p.sort));
  if (sorts.size > 1) {
    throw new SortMismatchError(`\`${op}\` mixes node-sort and edge-sort terms — split them or intersect within one sort`);
  }
  if (parts[0]?.sort === "edge") {
    const edgeSets = parts.map((p) => (p as { edges: Set<DependencyEdge> }).edges);
    return edge(op === "and" ? intersectRefs(edgeSets) : unionRefs(edgeSets));
  }
  const nodeSets = parts.map((p) => (p as { nodes: Set<string> }).nodes);
  return node(op === "and" ? intersectAll(nodeSets) : unionSets(nodeSets));
}

// =============================================================================
// Edge patterns (slice 3a: adjacency `>` + chains)
// =============================================================================

/**
 * A chain of hops → the hop-union of its per-hop arrows (design §1). Each hop
 * evaluates by its own operator (kinds may mix in a chain): `>` = adjacency,
 * `>>`/`<>` = the distance closure. The middle selector serves as both hop
 * endpoints (left-assoc chain collection).
 */
function evalArrow(sel: import("./selector-ast.js").SelArrow, ctx: EvalContext): Set<DependencyEdge> {
  const out = new Set<DependencyEdge>();
  let leftKeys = evalSide(sel.head, ctx);
  for (const hop of sel.hops) {
    const rightKeys = evalSide(hop.rhs, ctx);
    if (hop.op === ">") {
      for (const e of graphOf(ctx).edges) {
        if (e.kind === "call" && sourceMatches(e, leftKeys) && targetMatches(e, rightKeys)) out.add(e);
      }
    } else {
      const cl = closureOf(ctx);
      const from = toModuleVertexKeys(leftKeys, ctx.universe);
      const to = toModuleVertexKeys(rightKeys, ctx.universe);
      // `>>` unbounded = the cone; `<>` alone ≡ `<> within 1`.
      const within = hop.within ? withinValue(hop.within, ctx) : hop.op === "<>" ? 1 : Infinity;
      const mode: ClosureMode = hop.op === "<>" ? "undirected" : "directed";
      for (const e of cl.qualifyEdges({ from, to, within, mode })) out.add(e);
    }
    leftKeys = rightKeys;
  }
  return out;
}

/** Convert a node set to the module vertex keys the closure anchors on
 *  (interfaces/surfaces widen to their owning module; processes/spaces skip —
 *  process-side closure is a later slice). */
function toModuleVertexKeys(nodeKeys: Set<string>, u: NodeUniverse): Set<string> {
  const out = new Set<string>();
  for (const k of nodeKeys) {
    const n = u.byKey(k);
    if (!n) continue;
    if (n.structuralKind === "module") out.add(moduleKey(n.path));
    else if (n.structuralKind === "interface" || n.structuralKind === "surface") {
      const owner = owningModule(n, u);
      if (owner) out.add(moduleKey(owner.path));
    }
  }
  return out;
}

/** Nearest module ancestor of a nested element (longest module-kind path prefix). */
function owningModule(n: ElementNode, u: NodeUniverse): ElementNode | undefined {
  for (let len = n.path.length - 1; len >= 1; len--) {
    const anc = u.byKey(n.path.slice(0, len).join("."));
    if (anc?.structuralKind === "module") return anc;
  }
  return undefined;
}

/** The aspect atom's value with a `$knob` resolved against `ctx.knobs` (§3,
 *  S18). The bound value substitutes with the literal's own semantics — a
 *  string knob behaves as a classification value, an element knob as a place
 *  ref (`valueEquals` is lexform-agnostic, so the substitution IS the inline
 *  atom). An unbound or list-bound knob is a targeted error — never a silent
 *  empty set (mirrors `within $knob`). */
function aspectAtomValue(sel: SelAspect, ctx: EvalContext): Value | undefined {
  const v = sel.value;
  if (v === undefined || !("knob" in v)) return v;
  const atom = `@@${sel.path.join(".")}:$${v.knob}`;
  const bound = ctx.knobs?.get(v.knob);
  if (!bound) throw new SortMismatchError(`\`${atom}\` — knob \`${v.knob}\` is not bound to a value`);
  if (bound.kind === "list") {
    throw new SortMismatchError(`\`${atom}\` — knob \`${v.knob}\` binds a list; the aspect value slot takes a single value`);
  }
  return bound;
}

/** `within N` → the hop bound. A `$knob` bound resolves against `ctx.knobs`
 *  (§3, S18): the knob must be bound to a non-negative number. An unbound or
 *  non-numeric knob is a targeted error — never a silent wrong bound. */
function withinValue(w: WithinBound, ctx: EvalContext): number {
  return boundValue(w, ctx, "within");
}

/** The shared `within N` / `depth N` bound resolver. */
function boundValue(w: WithinBound, ctx: EvalContext, kw: "within" | "depth"): number {
  if (typeof w.value === "number") return w.value;
  const name = w.value.knob;
  const bound = ctx.knobs?.get(name);
  if (!bound) throw new SortMismatchError(`\`${kw} $${name}\` — knob \`${name}\` is not bound to a value`);
  if (bound.kind !== "number") throw new SortMismatchError(`\`${kw} $${name}\` — knob \`${name}\` must be a number, got ${bound.kind}`);
  return bound.value;
}

/**
 * `where ( <expr> )` as a node-sort set: every universe element for which the
 * expression is truthy (the element is the bound subject). `where` composes via
 * `and`/`or` like any node term. Edge-sort `where` (`@kind`) is a later slice.
 */
function evalWhere(expr: Expr, ctx: EvalContext): Set<string> {
  const out = new Set<string>();
  for (const n of ctx.universe.nodes) {
    // `this` inside the expression (getters, `exists (this …)`) binds to `n`.
    if (isTruthy(evalExpression(expr, n, { ...ctx, subject: n }))) out.add(n.key);
  }
  return out;
}

/** A side is a node-sort selector; evaluate it to its element keys. */
function evalSide(sel: Selector, ctx: EvalContext): Set<string> {
  const r = evalSelector(sel, ctx);
  if (r.sort !== "node") throw new SortMismatchError("an arrow side must be a node-sort selector");
  return r.nodes;
}

/** Call-edge source = the caller module. */
function sourceMatches(e: import("./graph.js").CallEdge, keys: Set<string>): boolean {
  return keys.has(e.fromModulePath.join("."));
}

/** Call-edge target = the callee interface, with owning-module widening (design §1). */
function targetMatches(e: import("./graph.js").CallEdge, keys: Set<string>): boolean {
  return keys.has(e.toInterfacePath.join(".")) || keys.has(e.toModulePath.join("."));
}

/**
 * `sources of` / `targets of` / `nodes of` / `owners of` `( <edge-sel> )` → a
 * node set. `owners of` = the processes that DERIVED the edges (every edge carries
 * its deriving process's identity); only named, selectable processes are yielded.
 */
function evalExtraction(sel: import("./selector-ast.js").SelExtraction, ctx: EvalContext): Set<string> {
  const inner = evalSelector(sel.inner, ctx);
  if (inner.sort !== "edge") {
    throw new SortMismatchError(`\`${sel.head} of\` needs an edge-sort selector`);
  }
  const out = new Set<string>();
  if (sel.head === "owners") {
    // The owning process's qualified name is the universe key for named
    // processes (anonymous ones aren't in the universe — unselectable).
    for (const e of inner.edges) {
      const owner = e.processName;
      if (owner && ctx.universe.byKey(owner)) out.add(owner);
    }
    return out;
  }
  for (const e of inner.edges) {
    if (e.kind !== "call") continue; // sources/targets/nodes are module↔interface (call edges)
    if (sel.head === "sources" || sel.head === "nodes") out.add(e.fromModulePath.join("."));
    if (sel.head === "targets" || sel.head === "nodes") out.add(e.toInterfacePath.join("."));
  }
  return out;
}

/**
 * `violating <PolicyRef>` — the set of elements/edges a named policy currently
 * flags (its **active**, non-waived findings; §4). Sort follows the policy: an
 * edge-only policy (`forbid <edge-sel>`) yields an edge set, everything else
 * (node forbids + `require`) a node set. The sort is read from ALL findings
 * (including waived ones, which retain their sort) so a fully-waived edge policy
 * still resolves to the empty EDGE set — right for composition. A policy with no
 * findings at all is unknowable and defaults to node-sort (empty). A policy that
 * mixes sorts (ill-formed — a later lint) resolves to its node findings.
 * Requires `ctx.policies` — an undeclared/out-of-scope policy name throws.
 */
function evalViolating(sel: SelViolating, ctx: EvalContext): EvalResult {
  const name = sel.policy.join(".");
  const policy = ctx.policies?.get(name);
  if (!policy) throw new UnsupportedSelectorError(sel); // no such policy in scope
  const all = evalPolicy(policy, ctx, ctx.now);
  const anyEdge = all.some((f) => f.sort === "edge");
  const anyNode = all.some((f) => f.sort === "node");
  const active = activeFindings(all);
  if (anyEdge && !anyNode) {
    const edges = new Set<DependencyEdge>();
    for (const f of active) if (f.edge) edges.add(f.edge);
    return edge(edges);
  }
  return node(new Set(active.filter((f) => f.sort === "node").map((f) => f.anchorKey)));
}

/**
 * `in <selector>` — containment / participation, unioned over every container
 * the selector names. `in <Module>` / `in <Surface>` = strict descendants
 * (path-prefix); `in <Space>` = elements whose declaring space is within it;
 * `in <Process>` = the elements that PARTICIPATE in the process (the endpoints
 * of its derived edges — the splice-inclusive action set, §1). `in this` reuses
 * whichever branch the bound subject's kind selects (needs a bound subject,
 * else throws).
 *
 * The argument is evaluated as an ordinary node selector — the SAME resolution
 * the bare atom and the view's `group by in <selector>` use, so `in service`
 * matches the kind, `in myQuery` the query, and one written name never means
 * two different things in two clauses of one view. A member of the resulting
 * set that nothing can be inside (a test) contributes nothing rather than
 * taking the whole selection down; that degradation is silent here because a
 * predicate has no clause to hang a warning on (`group by in` warns, VIEWS §4).
 */
function evalIn(arg: Selector, ctx: EvalContext, depth?: WithinBound): Set<string> {
  const u = ctx.universe;
  // `this` is the one argument that is not a selector over the universe: it
  // binds the subject the surrounding lens/policy rule dispatches on.
  if (arg.kind === "this") {
    if (!ctx.subject) throw new UnsupportedSelectorError(arg);
    return clipToDepth(containmentMembers(ctx.subject, ctx, arg), ctx.subject, depth, ctx);
  }
  const out = new Set<string>();
  for (const key of evalContainerSet(arg, ctx)) {
    const target = u.byKey(key);
    if (!target) continue;
    let members: Set<string>;
    try {
      members = containmentMembers(target, ctx, arg);
    } catch (e) {
      // Only "this container has no containment answer here" degrades — a kind
      // nothing can be inside, or a process in a graph-less context. Anything
      // else (an unbound knob inside the container selector, a sort mismatch)
      // still surfaces; `evalContainerSet` runs OUTSIDE this try for exactly
      // that reason.
      if (!(e instanceof UnsupportedSelectorError)) throw e;
      continue;
    }
    for (const k of clipToDepth(members, target, depth, ctx)) out.add(k);
  }
  return out;
}

/** The container side of `in` — node-sort, or a targeted error. */
function evalContainerSet(arg: Selector, ctx: EvalContext): Set<string> {
  const r = evalSelector(arg, ctx);
  if (r.sort !== "node") {
    throw new SortMismatchError("`in` takes a node-sort selector — an edge set names no container");
  }
  return r.nodes;
}

/**
 * Does a bare ref path NAME anything — a query, a built-in kind, a user type or
 * an element (scope-aware)? The `in`-target lint's question, answered with the
 * evaluator's own rule so the lint and {@link evalIn} never disagree: `in
 * service` selects every service, so it must not be badged "names nothing".
 */
export function refNamesSomething(path: QualifiedName, ctx: EvalContext): boolean {
  if (path.length === 1) {
    const name = path[0]!;
    if (BUILTIN_KINDS.has(name) || ctx.universe.isTypeName(name)) return true;
    if (ctx.queries?.has(name)) return true;
  }
  return resolveScopedRef(path, ctx.universe, ctx.scopePath) !== undefined;
}

/**
 * `depth N` clips the descent to N containment levels below the TARGET's own
 * depth — uniform across target kinds, so a space (depth 0) yields its
 * top-level modules and a module yields its children at `depth 1`. Per target,
 * so a multi-container `in (A or A.Core) depth 1` means "one level below each".
 */
function clipToDepth(
  members: Set<string>,
  target: ElementNode,
  depth: WithinBound | undefined,
  ctx: EvalContext,
): Set<string> {
  if (depth === undefined) return members;
  const u = ctx.universe;
  const limit = boundValue(depth, ctx, "depth") + containmentDepth(target, u);
  return new Set([...members].filter((k) => {
    const n = u.byKey(k);
    return n !== undefined && containmentDepth(n, u) <= limit;
  }));
}

/**
 * Everything the `in` predicate yields for ONE already-resolved target — the
 * body of `in <Ref>` with the ref resolution lifted out. Shared with the view's
 * `group by in <selector>` (VIEWS §4), which resolves its containers itself and
 * must ask the very same containment question the predicate answers.
 */
export function containmentMembers(target: ElementNode, ctx: EvalContext, site?: Selector): Set<string> {
  const u = ctx.universe;
  switch (target.structuralKind) {
    case "module":
    case "surface": {
      const prefix = target.path;
      const out = new Set<string>();
      for (const n of u.nodes) {
        if (n.path.length > prefix.length && prefix.every((seg, i) => n.path[i] === seg)) {
          out.add(n.key);
        }
      }
      return out;
    }
    case "space": {
      const out = new Set<string>();
      for (const n of u.nodes) {
        if (n.key !== target.key && spaceContains(target.path, n.space)) out.add(n.key);
      }
      return out;
    }
    case "interface":
      return new Set(); // interfaces are leaves — nothing is `in` them
    case "process":
    case "subprocess":
      return evalInProcess(target, ctx);
    default:
      throw new UnsupportedSelectorError(site ?? { kind: "ref", path: target.path });
  }
}

/**
 * `depth <N>` — containment depth from the model root: a top-level module is 1,
 * its submodule 2, and so on. Sub-elements (interfaces, surfaces, processes,
 * tests) take their OWNING MODULE's depth, so `show depth 1` keeps a top-level
 * module's ports instead of stripping them; spaces are not path segments of a
 * module, so a space sits at 0 and survives every bound.
 */
function evalDepth(limit: number, ctx: EvalContext): Set<string> {
  const u = ctx.universe;
  const out = new Set<string>();
  for (const n of u.nodes) if (containmentDepth(n, u) <= limit) out.add(n.key);
  return out;
}

/** How many MODULE segments a node's path carries (see {@link evalDepth}). */
function containmentDepth(n: ElementNode, u: NodeUniverse): number {
  let d = 0;
  for (let i = 1; i <= n.path.length; i++) {
    const at = i === n.path.length ? n : u.byKey(n.path.slice(0, i).join("."));
    if (at?.structuralKind === "module") d++;
  }
  return d;
}

/**
 * `in <Process>` participation — the endpoints of the edges the process derives
 * (matched by the edge's owning-process identity = the process's qualified name).
 * For a call edge that's the caller module, callee module, and callee interface;
 * for a `do` edge the spliced target process. Only elements present in the
 * universe are yielded. Needs the derived graph — throws without it.
 *
 * `UnsupportedSelectorError`, deliberately, so a multi-container `in` degrades
 * over it: a graph-less context is a legitimate one, not a harness fault. The
 * lane-selector path builds exactly that (`viewer-core/process-view-render.ts`
 * `laneEvalCtx`, `app-shell/processLaneDraft.ts`), and `lane "L": in *` must
 * keep filling from the module containers it CAN answer for instead of failing
 * the whole clause over the process it cannot.
 */
function evalInProcess(target: ElementNode, ctx: EvalContext): Set<string> {
  const u = ctx.universe;
  if (!ctx.graph) throw new UnsupportedSelectorError({ kind: "in", arg: { kind: "ref", path: target.path } });
  const out = new Set<string>();
  const addIfKnown = (key: string): void => {
    if (key && key !== target.key && u.byKey(key)) out.add(key);
  };
  for (const e of ctx.graph.edges) {
    if (e.processName !== target.key) continue;
    if (e.kind === "call") {
      addIfKnown(e.fromModulePath.join("."));
      addIfKnown(e.toModulePath.join("."));
      addIfKnown(e.toInterfacePath.join("."));
    } else if (e.toProcessName) {
      addIfKnown(e.toProcessName);
    }
  }
  return out;
}

/**
 * A bare `ref` atom. A single-part name that (a) names a query in scope and
 * (b) is NOT a built-in kind or a user type inlines that query's selector —
 * the query is named vocabulary, resolved in `ctx.queries` (the home package's
 * closure for an imported policy) while evaluating over `ctx.universe` (the
 * importer's data). Structural vocabulary (built-in kinds, user types) keeps
 * its meaning and is never shadowed by a homonymous query. Cyclic query
 * references throw {@link UnsupportedSelectorError}.
 */
function evalRef(sel: SelRef, ctx: EvalContext): EvalResult {
  const u = ctx.universe;
  if (sel.path.length === 1 && ctx.queries) {
    const name = sel.path[0]!;
    if (!BUILTIN_KINDS.has(name) && !u.isTypeName(name)) {
      const q = ctx.queries.get(name);
      if (q) {
        if (ctx._queryStack?.has(name)) throw new UnsupportedSelectorError(sel); // cyclic query
        const stack = new Set(ctx._queryStack ?? []);
        stack.add(name);
        return evalSelector(q.selector, { ...ctx, _queryStack: stack });
      }
    }
  }
  return node(evalRefOrType(sel.path, u, ctx.scopePath));
}

/** A bare identifier path: a built-in kind, a user type (incl. subtypes), or an element ref. */
function evalRefOrType(path: QualifiedName, u: NodeUniverse, scopePath?: QualifiedName): Set<string> {
  if (path.length === 1) {
    const name = path[0]!;
    if (BUILTIN_KINDS.has(name)) {
      return new Set(u.nodes.filter((n) => n.structuralKind === name).map((n) => n.key));
    }
    if (u.isTypeName(name)) {
      return new Set(u.nodes.filter((n) => n.typeChain.includes(name)).map((n) => n.key));
    }
  }
  const el = resolveScopedRef(path, u, scopePath);
  return el ? new Set([el.key]) : new Set();
}

/**
 * Resolve an element ref by qualified path: a module-scoped declaration resolves
 * from its module context first — the owner chain walked outward (§5.5 precedent) —
 * then the global fallback. Shared by the bare-ref atom, the `in` predicate's
 * target, the `$knob` element atom — and the view's `in`-target lint, which has
 * to answer "did this ref resolve?" with the very same rule the evaluator used.
 */
export function resolveScopedRef(path: QualifiedName, u: NodeUniverse, scopePath?: QualifiedName): ElementNode | undefined {
  if (scopePath && scopePath.length > 0) {
    for (let i = scopePath.length; i > 0; i--) {
      const el = u.resolveRef([...scopePath.slice(0, i), ...path]);
      if (el) return el;
    }
  }
  return u.resolveRef(path);
}

/**
 * `$knob` as an ELEMENT atom (§3, S18): the knob is bound to an element-ref value
 * in `ctx.knobs`, resolved to its node(s) via the universe exactly like a bare
 * `SelRef` (scope chain then global). A knob bound to a non-element value
 * (number / boolean / list / classification string), or not bound at all, is a
 * targeted error — never a silent empty set (mirrors `within $knob`).
 */
function evalKnobAtom(sel: SelKnob, ctx: EvalContext): Set<string> {
  const bound = ctx.knobs?.get(sel.name);
  if (!bound) throw new SortMismatchError(`\`$${sel.name}\` — knob \`${sel.name}\` is not bound to a value`);
  const path = elementRefPath(bound);
  if (!path) {
    throw new SortMismatchError(`\`$${sel.name}\` — knob \`${sel.name}\` is bound to a ${bound.kind}, not an element ref`);
  }
  const el = resolveScopedRef(path, ctx.universe, ctx.scopePath);
  return el ? new Set([el.key]) : new Set();
}

/** An element-ref knob value → its qualified path (`ref` path / bare `identifier`);
 *  any other value kind is not an element and yields `undefined`. */
function elementRefPath(v: Value): QualifiedName | undefined {
  if (v.kind === "ref") return [...v.path];
  if (v.kind === "identifier") return [v.text];
  return undefined;
}

/** `@@key` (has the aspect) / `@@key:value` (carries that value — list-aware). */
function evalAspect(keyPath: QualifiedName, value: Value | undefined, u: NodeUniverse): Set<string> {
  const key = keyPath.join(".");
  const out = new Set<string>();
  for (const n of u.nodes) {
    const vals = n.aspects.get(key);
    if (!vals || vals.length === 0) continue;
    if (value === undefined) {
      out.add(n.key);
    } else if (vals.some((v) => valueEquals(v, value))) {
      out.add(n.key);
    }
  }
  return out;
}

/**
 * `@deco` / `@deco:"v3"` — carriers of a decoration, the parity of
 * {@link evalAspect}. The valued form compares against the decoration's
 * PRIMARY argument, which is the single bare positional when written that way
 * and the `to`/`description` named arg otherwise.
 */
function evalDecorationAtom(
  name: string,
  value: Value | undefined,
  u: NodeUniverse,
  ctx: EvalContext,
): Set<string> {
  const out = new Set<string>();
  // `@lifecycle` is a builtin GETTER, not a decoration, so the carrier form
  // `@lifecycle:deprecated` found no decoration of that name and silently
  // matched nothing -- while `where (@lifecycle = deprecated)` worked and
  // `@deprecated:v3` worked. Answer it here as the state test it reads as.
  if (name === "lifecycle") {
    const want = value && textOf(value);
    if (want === undefined) return out;
    const t = ctx.scrubAt ?? u.now;
    for (const n of u.nodes) {
      const state = n.lifecycle ? lifecycleStateAt(n.lifecycle, t) : "live";
      if (state === want) out.add(n.key);
    }
    return out;
  }
  for (const n of u.nodes) {
    const inst = n.decorations?.get(name);
    if (!inst) continue;
    if (value === undefined) {
      out.add(n.key);
      continue;
    }
    // Through `argValue`, which owns the primary-field sugar. Spelling the rule
    // out per call site is how three separate readers each hardcoded `to`.
    const decl = u.decorations.kinds.get(name) ?? { name, fields: [] };
    const primary = argValue(inst, decl, primaryFieldOf(decl));
    if (primary && valueEquals(primary, value)) out.add(n.key);
  }
  return out;
}

function valueEquals(a: Value, b: Value): boolean {
  if (a.kind === "string" && b.kind === "string") return a.text === b.text;
  if (a.kind === "identifier" && b.kind === "identifier") return a.text === b.text;
  // A place ref (`@@zone:Dmz`) matches a string carrier of the same text and
  // vice-versa — the atom form is the containment test regardless of lexform.
  const at = textOf(a);
  const bt = textOf(b);
  return at !== undefined && at === bt;
}

function textOf(v: Value): string | undefined {
  if (v.kind === "string" || v.kind === "identifier") return v.text;
  if (v.kind === "number") return String(v.value);
  if (v.kind === "boolean") return String(v.value);
  return undefined;
}

function intersectAll(sets: Set<string>[]): Set<string> {
  if (sets.length === 0) return new Set();
  let acc = sets[0]!;
  for (let i = 1; i < sets.length; i++) {
    const next = sets[i]!;
    acc = new Set([...acc].filter((k) => next.has(k)));
  }
  return new Set(acc);
}

function unionSets(sets: Set<string>[]): Set<string> {
  const out = new Set<string>();
  for (const s of sets) for (const k of s) out.add(k);
  return out;
}

function intersectRefs<T>(sets: Set<T>[]): Set<T> {
  if (sets.length === 0) return new Set();
  let acc = sets[0]!;
  for (let i = 1; i < sets.length; i++) {
    const next = sets[i]!;
    acc = new Set([...acc].filter((e) => next.has(e)));
  }
  return new Set(acc);
}

function unionRefs<T>(sets: Set<T>[]): Set<T> {
  const out = new Set<T>();
  for (const s of sets) for (const e of s) out.add(e);
  return out;
}
